import type { RouteObject } from "react-router";
import type {
  FeatureNavModule,
  FeatureRouteModule,
  NavGroup,
  NavItem,
  ShellRouteDef,
} from "./types";
import { coreWorkRoutes, coreWorkNav } from "./features/core-work";
import { designSystemNav, designSystemRoutes } from "./features/design-system";
import { evalRoutes, evalNav } from "./features/eval";
import { studioRoutes, studioNav } from "./features/studio";
import { integrationsRoutes, integrationsNav } from "./features/integrations";
import { artifactsRoutes, artifactsNav } from "./features/artifacts";
import { settingsRoutes, settingsNav } from "./features/settings";
import { authRoutes, authNav } from "./features/auth";
import { qualityRoutes, qualityNav } from "./features/quality";
import { traceabilityRoutes, traceabilityNav } from "./features/traceability";
import { marketplaceRoutes, marketplaceNav } from "./features/marketplace";

/**
 * Feature route modules registered with the application.
 * To add a new feature or epic, add its module file under features/ and register it here with one line.
 */
export const featureRouteModules: FeatureRouteModule[] = [
  authRoutes,
  coreWorkRoutes,
  designSystemRoutes,
  evalRoutes,
  studioRoutes,
  integrationsRoutes,
  artifactsRoutes,
  settingsRoutes,
  qualityRoutes,
  traceabilityRoutes,
  marketplaceRoutes,
];

/**
 * Feature navigation modules registered with the application.
 * To add sidebar links or navigation groups, add one line here.
 */
export const featureNavModules: FeatureNavModule[] = [
  coreWorkNav,
  designSystemNav,
  evalNav,
  studioNav,
  integrationsNav,
  artifactsNav,
  settingsNav,
  authNav,
  qualityNav,
  traceabilityNav,
  marketplaceNav,
];

/**
 * Compose all public routes from registered modules.
 */
export function getRegisteredPublicRoutes(): RouteObject[] {
  return featureRouteModules.flatMap((mod) => mod.publicRoutes ?? []);
}

/**
 * Compose all authenticated shell routes from registered modules.
 */
export function getRegisteredShellRoutes(): RouteObject[] {
  return featureRouteModules.flatMap((mod) => mod.shellRoutes ?? []);
}

/**
 * Compose all shell route definitions for navigation/metadata.
 */
export function getRegisteredShellRouteDefs(): ShellRouteDef[] {
  return featureRouteModules.flatMap((mod) => mod.shellRouteDefs ?? []);
}

/**
 * Compose all primary navigation actions (e.g. "New run").
 */
export function getRegisteredPrimaryActions(): NavItem[] {
  return featureNavModules.flatMap((mod) => mod.primaryActions ?? []);
}

/**
 * Compose all navigation groups in sidebar order, deduplicating group labels.
 */
export function getRegisteredNavGroups(): NavGroup[] {
  const groupsByLabel = new Map<string, NavItem[]>();
  for (const mod of featureNavModules) {
    if (!mod.navGroups) continue;
    for (const group of mod.navGroups) {
      const existing = groupsByLabel.get(group.label);
      if (existing) {
        existing.push(...group.items);
      } else {
        groupsByLabel.set(group.label, [...group.items]);
      }
    }
  }
  return Array.from(groupsByLabel.entries()).map(([label, items]) => ({
    label,
    items,
  }));
}

/**
 * Compose settings item from registered modules.
 */
export function getRegisteredSettingsItem(): NavItem {
  for (const mod of featureNavModules) {
    if (mod.settingsItem) return mod.settingsItem;
  }
  return {
    to: "/settings",
    label: "Project settings",
    icon: settingsNav.settingsItem!.icon,
    end: false,
  };
}

/**
 * Compose the Marketplace footer item from registered modules.
 */
export function getRegisteredMarketplaceItem(): NavItem {
  for (const mod of featureNavModules) {
    if (mod.marketplaceItem) return mod.marketplaceItem;
  }
  return {
    to: "/marketplace",
    label: "Marketplace",
    icon: marketplaceNav.marketplaceItem!.icon,
    end: false,
  };
}

export * from "./types";
